import React, { useEffect, useState } from "react";
import { useAppQuery } from "../../../api/fetchClient";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { GET_BLOGS, GET_CATEGORIES, GETBLOG_CATEGORY } from "../../../api/url";
import { useNavigate } from "react-router-dom";
import { arrowBack, arrowForward } from "../../../assets/image";
import moment from "moment";
import { strings } from "../../../strings/strings";
import Loader from "../../../components/common/Loader";

const Blogs = () => {
  const [page, setPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState();
  const [modalOpen, setModalOpen] = useState();
  const [activeTab, setActiveTab] = useState("");
  const navigate = useNavigate();

  const { data: categoryData, isLoading: isCategoryLoading } = useAppQuery({
    queryKey: [QUERY_KEYS.CATEGORIES],
    url: `${GET_CATEGORIES}?type=blog`,
  });

const { data, isLoading } = useAppQuery({
  queryKey: [QUERY_KEYS.BLOG_CATEGORY, page, activeTab],
  url: activeTab ? `${GETBLOG_CATEGORY}?categoryId=${activeTab}` : `${GET_BLOGS}?page=${page}`,
  enabled: true, // Always enabled
});

const blogs = Array.isArray(data?.results?.data) ? data.results.data : [];

useEffect(() => {
  if (categoryData?.results?.length > 0 && activeTab === "") {
    setActiveTab(""); // Set to show "All blogs"
  }
}, [categoryData, activeTab]);
  const handleBlog = (blog) => {
    navigate("/admin/blog/blogDetails", { state: { blogId: blog._id } });
  };

  const handleUserProfile = (user) => {
    navigate("/admin/user/userDetails", { state: { userId: user._id } });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const { pagination } = data?.results ?? {};
  const { currentPage, totalPages } = pagination ?? {};

  if (isLoading || isCategoryLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-primary">Blogs</h1>
          <p className="mt-2 text-sm text-gray-700 capitalize">A list of all blog with category uploded by users.</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="flex gap-3 mb-6 min-w-max">
          <button
            onClick={() => handleTabChange("")}
            className={`text-sm font-medium py-2 px-4 rounded-md whitespace-nowrap ${
              activeTab === "" ? "bg-primary text-white" : "bg-white text-gray-700"
            }`}
          >
            All
          </button>

          {categoryData?.results?.map((cat) => (
            <button
              key={cat?._id}
              onClick={() => handleTabChange(cat?._id)}
              className={`text-sm font-medium py-2 px-4 rounded-md whitespace-nowrap ${
                activeTab === cat._id ? "bg-primary text-white" : "bg-white text-gray-700"
              }`}
            >
              {cat?.tittle}
            </button>
          ))}
        </div>
      </div>

      {blogs.length === 0 ? (
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <p className="text-center text-gray-500 text-lg">{strings.noBlogsAvailable}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {blogs.map((blog) => (
            <div
              key={blog?._id}
              className="bg-white rounded-2xl shadow transition-shadow duration-300 ease-in-out hover:shadow-lg cursor-pointer"
            >
              <div className="grid sm:flex gap-5 p-4">
                <img
                  src={blog?.blogImage}
                  alt={blog?.tittle}
                  onClick={() => {
                    handleBlog(blog);
                    setSelectedImage(blog?.blogImage);
                    setModalOpen(true);
                  }}
                  className="w-full sm:w-32 h-32 object-cover rounded-lg cursor-pointer"
                />
                <div className="flex flex-col justify-between flex-1">
                  <p className="font-semibold text-xl mb-2 capitalize line-clamp-1 text-primary cursor-pointer">
                    {blog.tittle}
                  </p>
                  <div onClick={() => handleUserProfile(blog?.user)} className="flex items-center gap-3">
                    {blog?.user?.profileImage && (
                      <img src={blog?.user?.profileImage} alt={blog?.user?.userName} className="w-8 h-8 rounded-full" />
                    )}
                    <p className="font-medium">{blog?.user?.userName || "Unknown Author"}</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-light">{moment(blog?.createdAt).fromNow()}</p>
                    <button
                      onClick={() => handleBlog(blog)}
                      className="bg-primary text-white text-sm sm:text-base font-semibold py-1 px-3 rounded-lg transition-colors duration-300 hover:bg-blue-600"
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <img
            src={selectedImage}
            alt="Blog"
            className="max-w-full max-h-full object-contain"
            onClick={() => setModalOpen(false)}
          />
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center space-x-2">
          <button
            onClick={() => setPage((old) => Math.max(old - 1, 1))}
            disabled={page === 1}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-md border text-sm font-medium ${
              page === 1
                ? "bg-gray-200 text-light cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-100 border-gray-300"
            }`}
          >
            <img src={arrowBack} alt="Previous" />
          </button>

          {Array.from({ length: totalPages }, (_, index) =>
            index < 2 || index > totalPages - 3 || Math.abs(page - (index + 1)) <= 1 ? (
              <button
                key={index}
                onClick={() => setPage(index + 1)}
                className={`px-3 sm:px-4 py-2 rounded-md border text-sm font-medium ${
                  currentPage === index + 1
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-gray-700 hover:bg-gray-100 border-gray-300"
                }`}
              >
                {index + 1}
              </button>
            ) : null
          )}

          <button
            onClick={() => setPage((old) => old + 1)}
            disabled={page === totalPages}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-md border text-sm font-medium ${
              page === totalPages
                ? "bg-gray-200 text-light cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-100 border-gray-300"
            }`}
          >
            <img src={arrowForward} alt="Next" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Blogs;
